<template>
  <section class="content white large-padding">
    <form class="hide-for-print" @submit.prevent="handleSubmit" spellcheck="false">
      <TheLoader :active="loading || exporting" :update="true"/>

      <fieldset style="margin: 0;">
        <div class="grid-x grid-padding-x">
          <TheAutoCompleteInput
            v-model.trim="form.code"
            ref="auksjon_input"
            name="Auksjon"
            containerClassName="cell large-2"
            :placeholder="'Velg auksjon'"
            :label="'Tilhørende auksjon'"
            :onInputCallback="onAuksjonSearchInput"
            :onSelectCallback="onAuksjonSearchSelected"
            :disabled="isDisabledCode"
            :required="isDisabledDate"
            :debounce="750"
            :minquery="2"
          >
            <div slot="item" slot-scope="props">
              <strong>{{props.item.Auksjon}}</strong>, {{props.item.Beskrivelse}}
              <span v-if="props.item.Auksjon_dato">
                ({{ props.item.Auksjon_dato | avantidate }})
              </span>
            </div>
          </TheAutoCompleteInput>

          <TheSelectInput
            v-model="form.avdelingsid"
            containerClassName="cell large-2"
            label="Avdeling"
            name="avdelingsid"
            :items="avdDropdown"
            :disabled="isDisabledDate"
            :required="isDisabledCode"
          />

          <div class="cell auto">
            <DateFromTillForm
              v-model="form.date"
              :labelFrom="'Dato f.o.m'"
              :labelTill="'Dato t.o.m'"
              :requiredFrom="isDisabledCode"
              :requiredTill="isDisabledCode"
              :disabled="isDisabledDate"
            />
          </div>

          <div class="cell auto">
            <label>&nbsp;</label>
            <template v-if="isValidForSubmit">
              <a v-if="!(loading || exporting)" href="javascript:;" @click="handleSubmit()" class="button primary submit tiny">Ok</a>
              <a v-else-if="loading" href="javascript:;" class="button primary submit tiny loading">Ok <span class="loader-spinner"></span></a>
              <a v-else href="javascript:;" class="button primary submit tiny" disabled>Ok</a>

              <!-- <template v-if="!found || !found.objects || found.objects.length < 1">
                <a href="javascript:;" class="button primary submit tiny" disabled style="margin-left: 0.25rem;">Exporter</a>
              </template>
              <template v-else-if="exporting">
                <a href="javascript:;" class="button primary submit tiny loading" style="margin-left: 0.25rem;">Exporter <span class="loader-spinner"></span></a>
              </template>
              <template v-else>
                <a @click="handleExport()" href="javascript:;" class="button primary submit tiny" style="margin-left: 0.25rem;">Exporter</a>
              </template> -->
            </template>
            <template v-else>
              <a href="javascript:;" class="button primary submit tiny" disabled>Ok</a>
            </template>
          </div>
        </div>
      </fieldset>
    </form>

    <div class="content-container flex-column">
      <div class="content-container flex-row" v-if="sectionData.length < 1">
        <em v-if="!searched" style="font-size: 0.75rem;" >
          Søk med hjelp av skjema oven...
        </em>
        <em v-else style="font-size: 0.75rem;">
          Fant ingen statistikk...
        </em>
      </div>
      <div v-else class="content-container flex-row content-x-scroll content-y-scroll table-results">
        <table class="large fixed sortable sticky-table-header compressed color-rows" style="height: 0">
          <thead>
            <tr>
              <th class="unsortable align-left"></th>
              <th class="unsortable align-center">Sum vurd. av alle</th>
              <th class="unsortable align-right">Solgte</th>
              <th class="unsortable align-right">Usolgte</th>
              <th class="unsortable align-right">Sum vurd. av solgte</th>
              <th class="unsortable align-right">Sum vurd. av usolgte</th>
              <th class="unsortable align-right">Sum solgte</th>
              <th class="unsortable align-right">Selger salær</th>
              <th class="unsortable align-right">Snitt tilslag</th>
              <th class="unsortable align-right">Kjøper salær</th>
              <th class="unsortable align-right">Sum kjp. + slg. salær</th>
              <th class="unsortable align-right">Foto solgte</th>
              <th class="unsortable align-right">Solgt ift. vurd. %</th>
              <th class="unsortable align-right">Usolgt antall %</th>
              <th class="unsortable align-right">Selger salær %</th>
              <th class="unsortable align-right">Solgt 1. gang</th>
              <th class="unsortable align-right">Solgt total</th>
              <th class="unsortable align-right">Omsetning</th>
              <th class="unsortable align-right">Bud</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="section in sectionData">
              <tr :key="'section-head-' + section.group" class="head">
                <td class="align-left"><strong>Kategori</strong></td>
                <td class="align-center"><strong>{{ section.title }}</strong></td>
                <td colspan="15">&nbsp;</td>
              </tr>

              <tr v-for="(item, index) in section.items" :key="'section-list-' + section.group + '-' + index" class="list">
                <td class="unsortable align-left">
                  <strong>{{ item.Kategori }}</strong> {{ item.Navn }}
                </td>
                <td class="unsortable align-center">{{ item.SumVurdAlle | thousands }}</td>
                <td class="unsortable align-right">{{ item.Solgte | thousands }}</td>
                <td class="unsortable align-right">{{ item.Usolgte | thousands }}</td>
                <td class="unsortable align-right">{{ item.SumVurdSolgte | thousands }}</td>
                <td class="unsortable align-right">{{ item.SumVurdUsolgte | thousands }}</td>
                <td class="unsortable align-right">{{ item.SumSolgte | thousands }}</td>
                <td class="unsortable align-right">{{ item.SelgerSalar | thousands }}</td>
                <td class="unsortable align-right" v-html="sumSolgte(item.SumSolgte, item.Solgte)"></td>
                <td class="unsortable align-right">{{ item.KjoperSalar | thousands }}</td>
                <td class="unsortable align-right">{{ item.SalarTotal | thousands }}</td>
                <td class="unsortable align-right">{{ item.FotoSolgte | thousands }}</td>
                <td class="unsortable align-right">{{ item.VurdSolgtePct | thousandsFloat }}</td>
                <td class="unsortable align-right">{{ item.AntallUsolgtePct | thousandsFloat }}</td>
                <td class="unsortable align-right">{{ item.SelgerSalarPct | thousandsFloat }}</td>
                <td class="unsortable align-right">{{ ((item.Solgt1Gang * 100) / item.TotalItems) | thousandsFloat }} %</td>
                <td class="unsortable align-right">{{ ((item.SolgtTotal * 100) / item.TotalItems) | thousandsFloat }} %</td>
                <td class="unsortable align-right">{{ (item.SumSolgte * 1.25) | thousands }}</td>
                <td class="unsortable align-right">{{ item.Bud }}</td>
              </tr>

              <tr :key="'section-totals-' + section.group" class="totals">
                <td class="unsortable align-left">Totaler:</td>
                <td class="unsortable align-center">{{ section.totals.SumVurdAlle | thousands }}</td>
                <td class="unsortable align-right">{{ section.totals.Solgte | thousands }}</td>
                <td class="unsortable align-right">{{ section.totals.Usolgte | thousands }}</td>
                <td class="unsortable align-right">{{ section.totals.SumVurdSolgte | thousands }}</td>
                <td class="unsortable align-right">{{ section.totals.SumVurdUsolgte | thousands }}</td>
                <td class="unsortable align-right">{{ section.totals.SumSolgte | thousands }}</td>
                <td class="unsortable align-right">{{ section.totals.SelgerSalar | thousands }}</td>
                <td class="unsortable align-right"></td>
                <td class="unsortable align-right">{{ section.totals.KjoperSalar | thousands }}</td>
                <td class="unsortable align-right">{{ section.totals.SalarTotal | thousands }}</td>
                <td class="unsortable align-right">{{ section.totals.FotoSolgte | thousands }}</td>
                <td colspan="4" class="unsortable align-right">&nbsp;</td>
              </tr>

              <tr :key="'section-breaker-' + section.group" class="breaker">
                <td colspan="13">&nbsp;</td>
              </tr>
            </template>

            <tr class="sums">
              <td class="unsortable align-left title">Sum total:</td>
              <td class="unsortable align-center">{{ sums.SumVurdAlle | thousands }}</td>
              <td class="unsortable align-right">{{ sums.Solgte | thousands }}</td>
              <td class="unsortable align-right">{{ sums.Usolgte | thousands }}</td>
              <td class="unsortable align-right">{{ sums.SumVurdSolgte | thousands }}</td>
              <td class="unsortable align-right">{{ sums.SumVurdUsolgte | thousands }}</td>
              <td class="unsortable align-right">{{ sums.SumSolgte | thousands }}</td>
              <td class="unsortable align-right">{{ sums.SelgerSalar | thousands }}</td>
              <td class="unsortable align-right"></td>
              <td class="unsortable align-right">{{ sums.KjoperSalar | thousands }}</td>
              <td class="unsortable align-right">{{ sums.SalarTotal | thousands }}</td>
              <td class="unsortable align-right">{{ sums.FotoSolgte | thousands }}</td>
              <td colspan="4" class="unsortable align-right">&nbsp;</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import StatistikkService from '@/services/StatistikkService'
import InstillingerService from '@/services/InstillingerService'
import AuksjonService from '@/services/AuksjonService'
import DateFromTillForm from '@/components/Inputs/DateFromTillForm'
import TheAutoCompleteInput from '@/components/Inputs/TheAutoCompleteInput'
import accounting from 'accounting-js'

export default {
  name: 'StatsSalgKategoriUng',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    DateFromTillForm,
    TheAutoCompleteInput
  },
  data () {
    return {
      loading: false,
      exporting: false,
      searched: false,
      sectionData: [],
      avdDropdown: [],
      sums: {},
      form: {
        avdelingsid: null,
        code: null,
        date: {
          from: null,
          till: null
        }
      }
    }
  },
  computed: {
    isDisabledCode: function () {
      return !!this.form.date.from || !!this.form.date.till || !!this.hasAvdelingsId
    },
    isDisabledDate: function () {
      return !!this.form.code
    },
    isValidForSubmit: function () {
      return this.form.code || (this.hasAvdelingsId && this.form.date.from && this.form.date.till)
    },
    hasAvdelingsId: function () {
      return !(this.form.avdelingsid === null || this.form.avdelingsid === '' || this.form.avdelingsid === undefined)
    }
  },
  created () {
    this.avdDropdown.unshift({key: this.form.avdelingsid, val: 'Laster...'})

    InstillingerService('avdeling').dropdown()
      .then(response => {
        this.avdDropdown = response.data || []
        this.avdDropdown.unshift({key: null, val: 'Velg avdeling', disabled: false})
      })
      .catch(error => {
        console.log(error)
      })
  },
  methods: {
    handleSubmit: function () {
      if (!this.isValidForSubmit) {
        return
      }

      // Make query
      const query = {}
      if (this.form.code) {
        query.code = this.form.code
      } else {
        query.from = this.form.date.from
        query.till = this.form.date.till
        query.avdelingsid = this.form.avdelingsid
      }

      this.sums = {}

      this.searched = false
      this.loading = true
      StatistikkService.loadCategoriesJson(query)
        .then(response => {
          this.sectionData = response.data

          // Calc totals
          this.sectionData.forEach(s => {
            Object.keys(s.totals).forEach(k => {
              if (!this.sums.hasOwnProperty(k)) {
                this.sums[k] = 0
              }

              this.sums[k] += Number(s.totals[k])
            })
          })
        })
        .catch(() => {
          this.sectionData = []
          this.sums = {}
        })
        .then(() => {
          this.loading = false
          this.searched = true
        })
    },
    scrollToTop: function () {
      window.jQuery('.table-results').scrollTop(0)
    },
    onAuksjonSearchInput: function (query) {
      return AuksjonService.searchInSettings(query)
    },
    onAuksjonSearchSelected: function (item) {
      if (item) {
        this.form.code = item.Auksjon || null
      } else {
        this.form.code = null
      }
    },
    sumSolgte: function (SumSolgte, Solgte) {
      let value = 0

      if (SumSolgte && Solgte) {
        value = SumSolgte / Solgte
      }
      if (value !== undefined && value !== null && value !== '') {
        return accounting.formatNumber(value, { precision: 0, thousand: ' ' })
      }
      return '0'
    }
  }
}
</script>
<style scoped>
  table.fixed tbody tr td:after {
    border: none !important;
    background-color: revert !important;
  }

  table.fixed tr td:first-child,
  table.fixed tr th:first-child {
    padding-left: 0;
  }

  table.fixed tr.totals {
    /* border-top: 1px solid #000; */
    /* border-bottom: 3px double #000; */
    -webkit-box-shadow: 0 -1px 0 0 rgb(0 0 0 / 52%), inset 0 -1px 0 0 rgb(0 0 0 / 52%) !important;
    box-shadow: 0 -1px 0 0 rgb(0 0 0 / 52%), inset 0 -1px 0 0 rgb(0 0 0 / 52%) !important;
  }

  table.fixed tr.totals td {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }

  table.fixed tr.totals td:first-child,
  table.fixed tr.sums td:first-child {
    font-weight: bold;
  }

  table.fixed tr.sums td {
    font-size: 0.75rem;
    font-weight: bold;
  }

  table.fixed tr.sums,
  table.fixed tr.breaker {
    border: none !important;
    box-shadow: none !important;
    -webkit-box-shadow: none !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/Admin/Reports/Stats/StatsSalgKategoriUng.vue